import React, { MouseEvent, useContext, useMemo } from "react";
import { groupBy, makeLink } from "simple-utils";
import { getAccountLink, getAdsLink, getBillingLink, getFranchise, monthGoal, goalClass, balanceKind, BalanceKind, isBoleto } from "../models/Account";
import { FullReportEntry, percentage } from "../models/FullReportEntry";
import { AccountTags } from "../tags/tags";
import { decimal, formatMoney } from "../utils/money-utils";
import { getRegion, stateName } from "../utils/state-utils";
import { AccountContext } from "./AccountContext";

export function formatStrDate(d:string){
    if(d == null) return ""
    return d.replace(/(\d{4})-(\d{2})-(\d{2})/, ' $3/$2/$1');
}

export function StatusRender({account}:{account:FullReportEntry}){
    function getClass(i:number){
        switch(i){
            case 0: return 'fa fa-check';
            case 3: return 'fa fa-exclamation-triangle text-warning';
            default: return 'fa fa-exclamation-triangle text-danger';
        }
    }

    function getLabel(i:number){
        if(account.trend == 'missing') return "Sem dados";
        switch(i){
            case 0: return 'Ativa';
            case 1: return 'Indefinida';
            case 3: return 'Período de Tolerância'
            default: return 'Desativada';
        }
    }

    return  <i title={getLabel(account.status) + formatStrDate(account.slDate)} className={getClass(account.status)}></i>
}

export function TrendRender({account}:{account:FullReportEntry}){
    function getClass(i:string){
        switch(i){
            case 'up': return 'fa fa-arrow-trend-up text-success';
            case 'down': return 'fa fa-arrow-trend-down text-danger';
            case 'missing': return 'fa fa-minus text-danger';
            default: return 'fa fa-minus';
        }
    }

    return <i className={getClass(account.trend)}></i>
}    

export function BalanceTag({account}:{account:FullReportEntry}){
    if(account.balance === null)
        return <></>;
    const min = account.dayBudget * 3;
    function getClass(){
        if(!isBoleto(account)) return '';
        if(account.balance < min) return 'badge bg-danger';
        if(account.balance < min * 2) return 'badge bg-warning';
        return 'badge bg-success'
    } 
    return <span className={getClass()}>{formatMoney(account.balance)}</span>
}

function DetailInfo({account}:{account:FullReportEntry}){
    const ctx = useContext(AccountContext)
    const detailInfoMode = ctx.detailInfoMode ?? "lead";
    const isTodayLeads = ctx.currentSorter?.id == 'leads-de-hoje';
    const {currentSorter} = ctx;

    if(currentSorter?.id == 'saldo'){
        return <div className="cost-info">
            <span className="current"><BalanceTag account={account} /></span>
        </div>    
    }

    if(currentSorter?.id && currentSorter.id.match(/meta/)){
        return <div className="cost-info">
            <span className="current" title={`Meta Geral: ${account.nLeads} / ${account.goal}`}>{decimal(monthGoal(account) * 100)}%</span>
            <span className="budget-goal" title="Meta Proporcional"><span className={`badge ${goalClass(account)}`}>{decimal(account.goalRatio * 100)}%</span></span>
        </div>    
    }

    if(detailInfoMode == 'cost') {
        return <div className="cost-info" onClick={(e) =>{e.stopPropagation(); ctx.dispatch({type: "setDetailInfoMode", payload: "lead"})}}>
            <span className="current">{formatMoney(account.totalCost)}</span>
            <span className="budget-goal">{formatMoney(account.budget)}</span>
        </div>    
    }

    if(ctx.googleMode)
        return <div className="number-lead" onClick={(e) => {e.stopPropagation();ctx.dispatch({type: "setDetailInfoMode", payload: "cost"})}}>
            <span className="current">{account.nLeads}</span>
            <span className="lead-goal">{account.reach}</span>
        </div>

    return <div className="number-lead" onClick={(e) => {e.stopPropagation();ctx.dispatch({type: "setDetailInfoMode", payload: "cost"})}}>
        <span className="current">{isTodayLeads ? account.todayLeads : account.nLeads}</span>
        <span className="lead-goal">{isTodayLeads ? Math.round(account.dayGoal) : account.goal}</span>
    </div>
}

export function AccountItem({account}:{account:FullReportEntry}){
    const ctx = useContext(AccountContext);
    const {dispatch, detailInfoMode, groupingOn} = ctx;
    const areaId = groupingOn ? makeLink(getRegion(account.state)) : "norte"
    const badgeClass = false ? `class-badge bg-${areaId}` : `color-${areaId}` 

    async function selectAccount(e:MouseEvent){
        if((e.ctrlKey || e.metaKey) && e.shiftKey){
            open(getBillingLink(account), account.metaId);
            return;
        }

        if(e.ctrlKey || e.metaKey){
            open(getAccountLink(account), account.metaId);
            return;
        }

        if(e.altKey){
            open(getAdsLink(account), account.metaId);
            return;
        }

        dispatch({type: "selectAccount", payload: account});
    }

    return <div className={`account-item ${ctx.account?.id == account.id ? 'selected' : ''}`} onClick={selectAccount}>
            <div className="account-item-body">
                <div className="info-col">
                    <div className={`${badgeClass} account-title-name`}><StatusRender account={account} /> {account.name} <TrendRender account={account} /></div>
                    <div className="franchise-label d-flex gap-2 align-items-center">{getFranchise(account)} <AccountTags account={account} /></div>
                </div>
                <DetailInfo  account={account} />
            </div>
            <div className={`goal-fill bg-light-${areaId}`}>
                <div className={`goal-percent bg-${areaId}`} style={{width: percentage(account, detailInfoMode)}}></div>
            </div>
        </div>
}

export function StateBlock({regionId, state, accounts}:{regionId:string, state: string, accounts: FullReportEntry[]}){
    return <div className="accounts-by-state">
        <div className="state-title"><span className={`color-${regionId}`}>{stateName(state)}</span> <span className={`bg-${regionId} ms-2`}>{accounts.length}</span></div>
        <div className="account-list">
            {accounts.map((a) => <AccountItem key={a.metaId} account={a} /> )}
        </div>
    </div>
}


export function RegionBlock({region, accounts}:{region:string, accounts:FullReportEntry[]}){
    const stateMap = useMemo(() => {
        return groupBy(accounts, c => c.state);
    }, [accounts]);

    const regionId = makeLink(region);

    return <div className="region-area">
        <div className="region-title">
            <span className={`bg-${regionId}`}>{region} <small className={`color-${regionId}`}>{accounts.length}</small></span>
        </div>
        {Object.entries(stateMap).map(([state, accounts]) => <StateBlock key={state} regionId={regionId} state={state} accounts={accounts} /> )}
    </div>
}